import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, Injector, OnDestroy } from '@angular/core';
import { CarrouselApiService } from '@bdo/carrousel';
import { CaptchaApiService, CaptchaDto, InteractionApiService, InteractionDto } from '@bdo/interaction';
import { RequestUrlApiService } from '@bdo/request-url';
import { SegmentationApiService, SendEmailApiService, SendEmailDto } from '@bdo/send-email';
import {
  CityApiService,
  CityDto,
  CompanyApiService,
  DepartmentApiService,
  DepartmentDto,
  TypeClientApiService,
  TypeClientDto,
  TypologyApiService,
} from '@bdo/typology';
import { NgxSpinnerService } from 'ngx-spinner';
import { EMPTY, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, finalize, map, takeUntil } from 'rxjs/operators';

import { FileEmail } from '../dynamic-form/models/file-email';
import { NotificationService } from '../dynamic-form/services/notification-service';
import { AuthenticationService } from '../services/authentication.service';
import { FileService } from '../shared/services/file-server/file-server.service';

import { CoreState } from './state/core.state';

/**
 * CoreFacade
 */
@Injectable()
export class CoreFacade implements OnDestroy {
  /**
   * Crea una nueva instancia de CoreFacade
   * @param injector Concrete injectors implement this interface.
   * @param companyApiService Consumo del servicio Company
   * @param typeClientApiService Consumo del servicio TypeClient
   * @param typologyApiService Consumo del servicio Typology
   * @param captchaApiService Consumo del servicio Captcha
   * @param sendEmailApiService Consumo del servicio SendEmail
   * @param authenticationService Consumo del servicio Authentication
   * @param requestUrlApiService Consumo del servicio RequestUrl
   * @param carrouselApiService Consumo del servicio Carrousel
   * @param segmentationApiService Consumo del servicio Segmentation
   * @param interactionApiService Consumo del servicio Interaction
   * @param cityApiService Consumo del servicio City
   * @param departmentApiService Consumo del servicio Department
   */
  public constructor(
    injector: Injector,
    cityApiService: CityApiService,
    companyApiService: CompanyApiService,
    typeClientApiService: TypeClientApiService,
    typologyApiService: TypologyApiService,
    captchaApiService: CaptchaApiService,
    sendEmailApiService: SendEmailApiService,
    authenticationService: AuthenticationService,
    requestUrlApiService: RequestUrlApiService,
    carrouselApiService: CarrouselApiService,
    segmentationApiService: SegmentationApiService,
    interactionApiService: InteractionApiService,
    departmentApiService: DepartmentApiService,
    notificationService: NotificationService,
    fileService: FileService,
    spinner: NgxSpinnerService,
  ) {
    this.companyApiService = companyApiService;
    this.spinner = spinner;
    this.typeClientApiService = typeClientApiService;
    this.typologyApiService = typologyApiService;
    this.captchaApiService = captchaApiService;
    this.sendEmailApiService = sendEmailApiService;
    this.authenticationService = authenticationService;
    this.requestUrlApiService = requestUrlApiService;
    this.carrouselApiService = carrouselApiService;
    this.segmentacionApiService = segmentationApiService;
    this.interactionApiService = interactionApiService;
    this.cityApiService = cityApiService;
    this.departmentApiService = departmentApiService;
    this.notificationService = notificationService;
    this.fileService = fileService;
    this.state = injector.get(CoreState);
  }
  /**
   * Observable para dar de baja las sucripciones
   */
  public destroy$ = new Subject<boolean>();
  /**
   * Administra el estado del modulo Core
   */
  private readonly state: CoreState;
  /**
   * Api que interactua con el servicio Company
   */
  private readonly companyApiService: CompanyApiService;
  /**
   * Spinner Service
   */
  public readonly spinner: NgxSpinnerService;
  /**
   * Api que interactua con el servicio TypeClient
   */
  private readonly typeClientApiService: TypeClientApiService;
  /**
   * Api que interactua con el servicio Typology
   */
  private readonly typologyApiService: TypologyApiService;
  /**
   * Api que interactua con el servicio Captcha
   */
  private readonly captchaApiService: CaptchaApiService;
  /**
   * Api que interactua con el servicio Segmentacion
   */
  private readonly segmentacionApiService: SegmentationApiService;
  /**
   * Api que interactua con el servicio SendEmail
   */
  private readonly sendEmailApiService: SendEmailApiService;
  /**
   * Servicio encargado de administrar la autenticación
   */
  private readonly authenticationService: AuthenticationService;
  /**
   * Servicio encargado de administrar la autenticación
   */
  private readonly interactionApiService: InteractionApiService;
  /**
   * Servicio encargado de interactuar con el microservicio de index georeferencia
   */
  private readonly requestUrlApiService: RequestUrlApiService;
  /**
   * Servicio encargado de interactuar con el microservicio de index georeferencia
   */
  private readonly carrouselApiService: CarrouselApiService;
  /**
   * Servicio encargado de notificar al usuario fallas en el servicio
   */
  private readonly notificationService: NotificationService;
  /**
   * Servicio encargado de interactuar con el microservicio de index georeferencia
   */
  private readonly cityApiService: CityApiService;
  /**
   * Servicio encargado de interactuar con el microservicio de index georeferencia
   */
  private readonly departmentApiService: DepartmentApiService;
  /**
   * Servicio encargado de interactuar con el FileServer
   */
  private readonly fileService: FileService;
  /**
   * Observable con Request Url
   */
  public readonly requestUrl$ = () => this.state.requestUrl$;
  /**
   * Observable con Carrousel
   */
  public readonly carrousel$ = () => this.state.carrousel$;
  /**
   * Observable con Company
   */
  public readonly companies$ = () => this.state.companies$;
  /**
   * Observable con TypeClient
   */
  public readonly typeClients$ = () => this.state.typeClients$;
  /**
   * Observable con Typology
   */
  public readonly typologies$ = () => this.state.typologies$;
  /**
   * Observable con ReCaptcha
   */
  public readonly reCaptcha$ = () => this.state.reCaptcha$;
  /**
   * Observable con ReCaptcha Gerente Elite
   */
  public readonly reCaptchaGe$ = () => this.state.reCaptchaGe$;
  /**
   * Observable del token auth
   */
  public readonly token$ = () => this.state.token$;
  /**
   * Observable del Radicado
   */
  public readonly radicado$ = () => this.state.radicado$;
  /**
   * Al destruirse la instancia de la fachada
   */
  public ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
    this.state.setCompanies([]);
    this.state.setTypeClients([]);
    this.state.setReCaptchGe({});
    this.state.setReCaptcha({});
  }

  /**
   * Metodo encargado de consultar las Compañías
   */
  public getCompanies(fn: () => void) {
    this.companyApiService
      .all()
      .pipe(
        map((data) => data.filter((x) => x.state === true)),
        catchError((error: HttpErrorResponse) => {
          const errorObs = this.handleError(error);
          fn();

          return errorObs;
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (response) => {
          this.state.setCompanies(response);
          fn();
        },
        error: () => {
          fn();
        },
      });
  }

  /**
   * Metodo encargado de consultar los Tipos de Clientes
   */
  public getTypeClients(fn: () => void) {
    this.typeClientApiService
      .all()
      .pipe(
        map((data) => data.filter((x) => x.state === true)),
        catchError((error: HttpErrorResponse) => {
          this.handleError(error);
          fn();

          return EMPTY;
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (response) => {
          this.state.setTypeClients(response);
          fn();
        },
        error: () => fn(), // Por si acaso algún error no es capturado por catchError
      });
  }

  public setTypeClients(type: TypeClientDto[]) {
    this.state.setTypeClients(type);
  }

  /**
   * Metodo encargado de consultar las Tipologías
   */
  public getTypologies(idCompany?: number, idTypeClient?: number) {
    this.state.setTypologies([]);
    this.typologyApiService
      .all()
      .pipe(
        catchError((error: HttpErrorResponse) => {
          this.handleError(error);

          return EMPTY;
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (response) => {
          if (idCompany && idTypeClient) {
            const output = response.filter((e) => e.idCompany === idCompany && e.idTypeClient === idTypeClient);
            this.state.setTypologies(output);
          }
        },
      });
  }

  /**
   * Metodo encargado de consultar las Tipología y retornar la información
   */
  public getTypologiesGe(idCompany?: number, idTypeClient?: number) {
    this.state.setTypologies([]);

    return this.typologyApiService.all().pipe(
      catchError((error: HttpErrorResponse) => {
        this.handleError(error);

        return EMPTY;
      }),
      takeUntil(this.destroy$),
      map((response) => {
        if (idCompany && idTypeClient) {
          return response.filter((e) => e.idCompany === idCompany && e.idTypeClient === idTypeClient);
        }

        return response;
      }),
    );
  }

  /**
   * Metodo encargado de consultar si es  cliente o no
   */
  public validateClient(idCompany: number, document: string, fn: (data: string) => void) {
    this.state.setTypologies([]);
    this.segmentacionApiService
      .GetDocumentState(document, idCompany)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          this.handleError(error);
          fn('error');

          return EMPTY;
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (response: string) => {
          fn(response);
        },
      });
  }

  /**
   * Metodo encargado de Enviar Captcha
   */
  public postRecaptcha(data: CaptchaDto) {
    this.captchaApiService
      .postCaptcha(data)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          this.handleError(error);
          // Establecer valores por defecto en caso de error
          this.state.setReCaptcha({ isValidAuthentication: false });
          this.state.setToken('');
          this.authenticationService.setAuthenticated(false);
          this.authenticationService.setToken = '';

          return EMPTY;
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (response) => {
          this.state.setReCaptcha(response);
          this.state.setToken(response.authenticationToken || '');
          this.authenticationService.setAuthenticated(response.isValidAuthentication || false);
          this.authenticationService.setToken = response.authenticationToken || '';
        },
      });
  }

  /**
   * Metodo encargado de Enviar Captcha
   */
  public postRecaptchaGe(data: CaptchaDto) {
    this.captchaApiService
      .postCaptcha(data)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          this.handleError(error);
          // Establecer valores por defecto en caso de error
          this.state.setReCaptchGe({ isValidAuthentication: false });
          this.state.setToken('');
          this.authenticationService.setAuthenticated(false);
          this.authenticationService.setToken = '';

          return EMPTY;
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (response) => {
          this.state.setReCaptchGe(response);
          this.state.setToken(response.authenticationToken || '');
          this.authenticationService.setAuthenticated(response.isValidAuthentication || false);
          this.authenticationService.setToken = response.authenticationToken || '';
        },
      });
  }

  /**
   * Metodo encargado de Enviar Datos de Correo
   */
  public postSendEmail(data: SendEmailDto, fn: () => void) {
    this.sendEmailApiService
      .PostSendEmail(data)
      .pipe(takeUntil(this.destroy$))
      .subscribe((response) => {
        if (response.radicado) {
          const radicado = `${data.radicado}`;
          this.state.setRadicado(radicado);
          fn();
        }
      });
  }

  /**
   * Metodo encargado de  traer  las urls de las redes  sociales
   */
  public getRequestUrl(fn: () => void) {
    this.requestUrlApiService
      .all()
      .pipe(
        map((data) => data.filter((x) => x.state === true)),
        catchError((error: HttpErrorResponse) => {
          this.handleError(error);
          fn();

          return EMPTY;
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (response) => {
          this.state.setRedesSociales(response);
          fn();
        },
        error: () => fn(), // Por si acaso algún error no es capturado por catchError
      });
  }

  /**
   * Metodo encargado de  traer  las imagenes de carrousel
   */
  public getCarrousel(fn: () => void) {
    this.carrouselApiService
      .all()
      .pipe(
        map((data) => data.filter((x) => x.state === true)),
        catchError((error: HttpErrorResponse) => {
          this.handleError(error);
          fn();

          return EMPTY;
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (response) => {
          this.state.setCarrousel(response);
          fn();
        },
        error: () => fn(), // Por si acaso algún error no es capturado por catchError
      });
  }

  public saveFileStorage(files: File[], fn: (result: string) => void) {
    if (files.length > 0) {
      const filesEmail: FileEmail[] = [];
      this.fileService.PostUpdateFiles(files).subscribe((response) => {
        console.log('Entro al PostUpdates', response);
        response.forEach((item) => {
          const fileEmail: FileEmail = {
            name: item.fileInfo?.name,
            uriDownload: item.fileInfo?.path?.uriDownload,
          };
          filesEmail.push(fileEmail);
        });
        fn(JSON.stringify(filesEmail));
      });
    } else {
      fn('');
    }
  }

  /**
   * Metodo encargado de guardar la interación
   * @param interaction Representa la informacion de InteractionDto
   */
  public saveInteraction(interaction: InteractionDto, fn: (flag: string) => void) {
    this.interactionApiService
      .create(interaction)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          this.handleError(error);
          fn('false');

          return EMPTY;
        }),
        finalize(() => {
          this.spinner.hide();
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (response) => {
          console.log(response);
          if (response.id) {
            const radicado = response.radicado || '';
            this.state.setRadicado(radicado);
            fn(radicado);
          } else {
            fn('false');
          }
        },
        error: () => fn('false'),
      });
  }

  /**
   * Método encargado de consultar todos los departamentos
   */
  public departmentsAll(): Observable<DepartmentDto[]> {
    this.state.setLoading(true);

    return this.departmentApiService.all().pipe(
      catchError((error: HttpErrorResponse) => {
        this.handleError(error);
        this.state.setLoading(false);

        return of([] as DepartmentDto[]);
      }),
      finalize(() => this.state.setLoading(false)),
    );
  }

  /**
   * Método encargado de consultar todas las ciudades
   */
  public citiesAll(): Observable<CityDto[]> {
    this.state.setLoading(true);

    return this.cityApiService.all().pipe(
      catchError((error: HttpErrorResponse) => {
        this.handleError(error);
        this.state.setLoading(false);

        return of([] as CityDto[]);
      }),
      finalize(() => this.state.setLoading(false)),
    );
  }

  /**
   * Handle errors and show notifications to the user
   * @param error error in the request
   * @returns messsage the client
   */
  private handleError(error: HttpErrorResponse): Observable<never> {
    const errorMessages: Record<number, string> = {
      401: 'Estamos presentando intermitencia en el servicio, lo resolveremos pronto. Puedes contactarnos a nuestras líneas telefónicas en Bogotá o a nivel nacional (ver parte inferior de la página)',
      503: 'Estamos presentando intermitencia en el servicio, lo resolveremos pronto. Puedes contactarnos a nuestras líneas telefónicas en Bogotá o a nivel nacional (ver parte inferior de la página)',
      500: 'Estamos presentando intermitencia en el servicio, lo resolveremos pronto. Puedes contactarnos a nuestras líneas telefónicas en Bogotá o a nivel nacional (ver parte inferior de la página)',
      400: 'Estamos presentando intermitencia en el servicio, lo resolveremos pronto. Puedes contactarnos a nuestras líneas telefónicas en Bogotá o a nivel nacional (ver parte inferior de la página)',
      404: 'Estamos presentando intermitencia en el servicio, lo resolveremos pronto. Puedes contactarnos a nuestras líneas telefónicas en Bogotá o a nivel nacional (ver parte inferior de la página)',
    };

    const message =
      errorMessages[error.status] ||
      'Estamos presentando intermitencia en el servicio, lo resolveremos pronto. Puedes contactarnos a nuestras líneas telefónicas en Bogotá o a nivel nacional (ver parte inferior de la página)';
    // Eslint-disable-next-line @typescript-eslint/no-unused-expressions
    error.status === 404 ? this.notificationService.showWarning(message) : this.notificationService.showError(message);

    return throwError(() => error);
  }
}
